import {
  BigIntWithDecimals,
  DEFAULT_DECIMALS,
  NATIVE_CURRENCY_DECIMALS,
  PRECISION_DECIMALS,
} from "@silo/types";
import { shiftDecimal } from "./bigint";
import { pipe } from "effect";
import { truncateDecimals } from "./string";
import * as t from "io-ts";
import { chain } from "fp-ts/lib/Either";
import { NonEmptyString } from "io-ts-types";
import { formatUnits, parseUnits } from "@ethersproject/units";
import * as Monoid from "fp-ts/Monoid";
import { bigIntSum, last } from "./monoid";

/**
 * Converts value from currency A to currency B given:
 *
 * amount: atomic units of currency A
 * price: atomic units of currency B per unit of A
 */
export const convertCurrency = (
  amount: BigIntWithDecimals,
  price: BigIntWithDecimals
): BigIntWithDecimals => ({
  decimals: price.decimals,
  value: shiftDecimal(amount.value * price.value, -amount.decimals),
});

/**
 * Parses a string into a `BigIntWithDecimals`
 */
export const parseBigIntWithDecimals =
  (decimals: number) =>
  (str: string): BigIntWithDecimals =>
    // extra decimals are used to avoid an ethers underflow error
    pipe(
      str,
      truncateDecimals,
      (value) => parseUnits(value, PRECISION_DECIMALS).toBigInt(),
      (value) => shiftDecimal(value, decimals - PRECISION_DECIMALS),
      (value) => ({ value, decimals })
    );

export interface BigIntWithDecimalsFromString
  extends t.Type<BigIntWithDecimals, string, unknown> {}

export const BigIntWithDecimalsFromString = (
  decimals = DEFAULT_DECIMALS
): BigIntWithDecimalsFromString =>
  new t.Type<BigIntWithDecimals, string, unknown>(
    "BigIntWithDecimalsFromString",
    BigIntWithDecimals.is,
    (u, c) =>
      pipe(
        t.string.validate(u, c),
        chain((s) => {
          if (!NonEmptyString.is(s.trim())) {
            return t.failure(u, c);
          }
          try {
            return t.success(parseBigIntWithDecimals(decimals)(s));
          } catch (error) {
            return t.failure(u, c);
          }
        })
      ),
    ({ value, decimals }) => formatUnits(value, decimals)
  );

const fromString = (
  str: string,
  decimals = DEFAULT_DECIMALS
): BigIntWithDecimals => ({
  value: toBigInt(str, decimals),
  decimals,
});

export const createBigIntWithDecimals = { fromString };

/**
 * Parses a string to a `bigint` - similar to ethers `parseUnits`
 */
export const toBigInt = (value: string, units = 18): bigint =>
  parseBigIntWithDecimals(units)(value).value;

export const BigIntWithDecimalsSum = Monoid.struct<BigIntWithDecimals>({
  value: bigIntSum,
  decimals: last(NATIVE_CURRENCY_DECIMALS),
});

/**
 * Converts value from currency A to currency B given:
 *
 * amount: atomic units of currency A
 * price: atomic units of currency A per unit of B
 * targetDecimals: decimals of currency B
 */
export const convertCurrencyWithPriceOfTarget = (
  amount: BigIntWithDecimals,
  price: BigIntWithDecimals,
  targetDecimals: number
): BigIntWithDecimals => {
  if (amount.decimals !== price.decimals) {
    throw new Error("Amount and price should have the same number of decimals");
  }

  const decimalAdjustment = Math.max(
    0,
    PRECISION_DECIMALS - amount.decimals - targetDecimals
  );
  const value =
    shiftDecimal(amount.value, targetDecimals + decimalAdjustment) /
    price.value;

  return {
    value: shiftDecimal(value, -decimalAdjustment),
    decimals: targetDecimals,
  };
};
